import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import { debounce } from 'lodash'
import toast from 'react-hot-toast'
import { useConfigUI } from '../../context'
import { useDeleteHydraulic } from '../../hooks/hydraulic'
import useFlowRuleCascadeCheck from '../../hooks/hydraulic/useFlowRuleCascadeCheck'
import CascadeDeleteNotice from './CascadeDeleteNotice'
import useTranslation from '@/hooks/useTranslation'

interface DeleteModalProps {
  id?: string
  interfaceId?: string
  name?: string
  flowRuleId?: string
  /** Sub-pipes the delete takes with it, said in the dialog. */
  subPipeCount?: number
  /**
   * Run once the pipe is gone: the page drops the pipe's pending edits, which
   * would otherwise be re-sent (404) and hold the step (round 7 review, H2).
   */
  onSuccess?: () => void | Promise<void>
}

const DeleteHydraulicModal = ({
  id,
  interfaceId,
  name,
  flowRuleId,
  subPipeCount = 0,
  onSuccess,
}: DeleteModalProps) => {
  const { t } = useTranslation()
  const { closeModal } = useConfigUI()

  const { mutateAsync: deleteModal, isLoading: loadingDeleteHydraulic } =
    useDeleteHydraulic()

  // Looked up before the user agrees, and handed to the delete, so what is
  // removed is exactly what the confirmation named. Read fresh on every
  // opening, and Confirm waits for it and for the water sources.
  const {
    children,
    childrenForDelete,
    loading: loadingChildren,
    blockReason,
  } = useFlowRuleCascadeCheck(interfaceId, flowRuleId)

  const handleDeleteHydraulic = debounce(async () => {
    if (!id) return
    if (!interfaceId) return
    // A valve or sensor on the pipe's rule that a water source uses would go
    // with the pipe; it is refused here, naming the water sources.
    const blocked = blockReason()
    if (blocked) {
      toast.error(blocked)
      closeModal()
      return
    }
    // Awaited rather than passed as a mutate callback: the hook's own
    // onSuccess closes this modal, and React Query drops call-site callbacks
    // once the component has unmounted.
    try {
      await deleteModal({
        interfaceId,
        id,
        flowRuleId,
        children: childrenForDelete,
      })
    } catch {
      // The hook already reports the failure.
      return
    }
    await onSuccess?.()
  }, 300)

  return (
    <ConfirmDeleteModalUI
      title={t('DeleteHydraulic')}
      name={name}
      text={
        subPipeCount > 0
          ? `${t('dataConfigThisActionIsNotReversibleThisHydraulicDataWillBeDeleted')} ${t('hydraulicDeleteAlsoSubPipes', { count: subPipeCount })}`
          : t('dataConfigThisActionIsNotReversibleThisHydraulicDataWillBeDeleted')
      }
      onClose={closeModal}
      onConfirm={handleDeleteHydraulic}
      isLoading={loadingDeleteHydraulic || loadingChildren}
    >
      <CascadeDeleteNotice
        children={children}
        includesFlowRule={Boolean(flowRuleId)}
        isLoading={Boolean(flowRuleId) && loadingChildren}
      />
    </ConfirmDeleteModalUI>
  )
}

export default DeleteHydraulicModal
